import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, CheckCircle, XCircle, Clock, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface InviteData {
  email: string;
  shop_name?: string;
  full_name?: string;
  expires_at: string;
  /**
   * Lead-role string the admin set when issuing the invite. Display-
   * only; the authoritative role is the membership row created by
   * provision-beta-org. Used downstream by PassportHome to pre-lock
   * the Passport role picker so the invited user can't free-select.
   */
  role_requested?: string | null;
}

type InviteError = 'invalid' | 'expired' | 'revoked' | 'already_provisioned' | null;

export default function BetaInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [error, setError] = useState<InviteError>(null);
  const [checkingSession, setCheckingSession] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('invalid');
      setLoading(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke('validate-beta-invite', {
        body: { token }
      });

      if (fnError) throw fnError;

      if (!data.valid) {
        setError(data.error);
        setLoading(false);
        return;
      }

      setInviteData({
        email: data.email,
        shop_name: data.shop_name,
        full_name: data.full_name,
        expires_at: data.expires_at,
        role_requested: data.role_requested ?? null,
      });
      setLoading(false);
    } catch (err) {
      console.error('Error validating invite:', err);
      setError('invalid');
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!inviteData || !token) return;

    setCheckingSession(true);

    // Store invite data in localStorage for use after auth.
    //
    // `role_requested` is included for display-only purposes (the
    // PassportHome lock-mode helper text can say "your invited role is
    // X"). It is NOT trusted by provision-beta-org — that function
    // re-reads the row from beta_invites by token. Server-verified
    // membership is the authoritative role downstream.
    localStorage.setItem('beta_invite_data', JSON.stringify({
      token,
      email: inviteData.email,
      shop_name: inviteData.shop_name,
      full_name: inviteData.full_name,
      role_requested: inviteData.role_requested ?? null,
    }));

    // Check if user is already logged in
    const { data: { session } } = await supabase.auth.getSession();

    if (session) {
      // User is logged in, go directly to complete
      navigate('/beta/complete');
    } else {
      // User needs to sign up/sign in
      navigate(`/auth?mode=signup&beta=1&email=${encodeURIComponent(inviteData.email)}`);
    }
  };

  const getErrorContent = () => {
    switch (error) {
      case 'expired':
        return {
          icon: <Clock className="h-5 w-5" />,
          title: "Invite Expired",
          description: "This beta invite link has expired. Please contact support for a new invite.",
        };
      case 'revoked':
        return {
          icon: <XCircle className="h-5 w-5" />,
          title: "Invite Revoked",
          description: "This beta invite has been revoked. Please contact support if you believe this is an error.",
        };
      case 'already_provisioned':
        return {
          icon: <CheckCircle className="h-5 w-5" />,
          title: "Already Used",
          description: "This invite has already been used. If you need help accessing your account, please contact support.",
        };
      default:
        return {
          icon: <XCircle className="h-5 w-5" />,
          title: "Invalid Invite",
          description: "This beta invite link is not valid. Please check the link or contact support.",
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Validating your invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const errorContent = getErrorContent();
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              {errorContent.icon}
            </div>
            <CardTitle>{errorContent.title}</CardTitle>
            <CardDescription>{errorContent.description}</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button variant="outline" asChild>
              <a href="mailto:support@optibayai.com">Contact Support</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!inviteData) return null;

  const expiresDate = new Date(inviteData.expires_at);
  const daysUntilExpiry = Math.ceil((expiresDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <CheckCircle className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Welcome to OptiBay Beta</CardTitle>
          <CardDescription>
            You've been invited to join the OptiBay AI beta program
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3 rounded-lg border bg-muted/50 p-4">
            {inviteData.shop_name && (
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <span className="text-sm font-semibold text-primary">
                    {inviteData.shop_name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Shop</p>
                  <p className="font-medium">{inviteData.shop_name}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <Mail className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{inviteData.email}</p>
              </div>
            </div>
          </div>

          {daysUntilExpiry <= 3 && (
            <Alert variant="destructive">
              <Clock className="h-4 w-4" />
              <AlertTitle>Expires Soon</AlertTitle>
              <AlertDescription>
                This invite expires on {format(expiresDate, 'PPP')}. Please complete your registration soon.
              </AlertDescription>
            </Alert>
          )}

          <Button 
            className="w-full" 
            size="lg" 
            onClick={handleContinue}
            disabled={checkingSession}
          >
            {checkingSession ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Please wait...
              </>
            ) : (
              "Continue"
            )}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
